/**
 * @file context/SocketContext.jsx
 * @description Site-wide Socket.IO connection manager.
 *
 * Responsibilities:
 *  - Connect to the backend Socket.IO server once the user is authenticated,
 *    passing the MSAL access token in the handshake for server-side validation.
 *  - Track connection state (`connected`) and expose the raw `socket` instance
 *    so any component can emit or listen to events via `useSocket()`.
 *  - Send application-level pings every PING_INTERVAL_MS to detect stale
 *    connections that socket.io's own heartbeat might miss.
 *  - Show a `ConnectionLostModal` after DISCONNECT_MODAL_DELAY_MS of being
 *    offline, giving the user the option to wait or force-reload.
 *  - Silently hide the modal when the connection is restored.
 *
 * Usage:
 *   const { socket, connected } = useSocket();
 *   socket?.emit('some-event', payload);
 *   socket?.on('some-event', handler);
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useMsal } from '@azure/msal-react';
import { io as socketIo } from 'socket.io-client';

import { tokenRequest } from '../auth/msalConfig';
import ConnectionLostModal from '../components/ConnectionLostModal';

// ── Configuration ─────────────────────────────────────────────────────────────

/** How often to send an application-level ping (ms). */
const PING_INTERVAL_MS = 30_000;

/**
 * How long to wait after a disconnect before showing the connection-lost modal.
 * A short debounce avoids flashing the modal on transient reconnects.
 */
const DISCONNECT_MODAL_DELAY_MS = 3_000;

/**
 * Number of consecutive authentication-related connect_errors before we give
 * up retrying automatically and tell the user to reload instead. Guards
 * against an infinite retry loop when the user's session can no longer be
 * silently renewed (e.g. they were signed out, or MSAL's own session expired).
 */
const MAX_AUTH_FAILURES = 3;

// ── Context & hook ────────────────────────────────────────────────────────────

const SocketContext = createContext(null);

/**
 * Hook for consuming the socket context.
 * Must be used inside a <SocketProvider>.
 *
 * @returns {{ socket: import('socket.io-client').Socket | null, connected: boolean }}
 */
export function useSocket() {
  const ctx = useContext(SocketContext);
  if (!ctx) throw new Error('useSocket must be used inside <SocketProvider>');
  return ctx;
}

// ── Provider ──────────────────────────────────────────────────────────────────

/**
 * @param {Object}          props
 * @param {React.ReactNode} props.children
 */
export function SocketProvider({ children }) {
  const { instance, accounts } = useMsal();

  const [socket, setSocket]           = useState(null);
  const [connected, setConnected]     = useState(false);
  const [showLostModal, setShowLostModal] = useState(false);
  // True once we've given up retrying and the user must reload manually
  const [unrecoverable, setUnrecoverable] = useState(false);

  /** Ref to the disconnect-modal delay timer so it can be cancelled on reconnect. */
  const modalTimerRef = useRef(null);
  /** Ref to the keepalive ping interval. */
  const pingIntervalRef = useRef(null);
  /** Counts consecutive connect_error events caused by authentication failures. */
  const authFailureCountRef = useRef(0);

  /**
   * Acquires a fresh Entra ID access token silently via MSAL.
   * Used to authenticate the socket handshake.
   *
   * Reads the account from `instance` rather than closing over `accounts` — the
   * array from useMsal() gets a new identity on most renders, and depending on
   * it here would churn this callback and, through it, the connect effect below.
   *
   * @returns {Promise<string|null>}
   */
  const getAccessToken = useCallback(async () => {
    const account = instance.getAllAccounts()[0];
    if (!account) return null;
    try {
      const response = await instance.acquireTokenSilent({ ...tokenRequest, account });
      return response.accessToken;
    } catch {
      return null;
    }
  }, [instance]);

  // ── Connect / disconnect lifecycle ─────────────────────────────────────────
  // Keyed on the account id, not the accounts array — the array's identity
  // changes on most renders, which would tear down and rebuild the socket each
  // time and surface as "WebSocket is closed before the connection is
  // established" in the console.
  const accountId = accounts[0]?.homeAccountId ?? null;

  useEffect(() => {
    // Only connect when an authenticated user is present
    if (!accountId) return undefined;

    // Created synchronously so the cleanup below always has a socket to close.
    // Building it inside an async callback meant StrictMode's immediate
    // unmount could run cleanup before the variable was assigned, leaving an
    // orphaned connection that nothing could ever disconnect.
    const s = socketIo(import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001', {
      // auth as a FUNCTION (not a static object) is re-invoked by socket.io on
      // every single connection attempt, including automatic reconnects. This
      // is required for token expiry to be recoverable: a static { token }
      // object would keep sending the same now-expired JWT on every retry
      // forever, since socket.io never re-reads it. acquireTokenSilent()
      // transparently renews the access token via MSAL's refresh token, so
      // most expiries are fixed by simply fetching a fresh token per attempt.
      auth: async (callback) => {
        const token = await getAccessToken();
        callback({ token });
      },

      // socket.io built-in reconnection settings
      reconnection:          true,
      reconnectionAttempts:  Infinity, // keep trying forever
      reconnectionDelay:     1_000,    // start at 1 s
      reconnectionDelayMax:  10_000,   // cap at 10 s
      randomizationFactor:   0.3,

      // Prefer WebSocket, fall back to long-polling only if needed
      transports: ['websocket', 'polling'],
    });

    // ── Event: connected ──────────────────────────────────────────────────
    s.on('connect', () => {
      setConnected(true);
      setUnrecoverable(false);
      authFailureCountRef.current = 0;
      // Cancel the modal timer — we're back online
      clearTimeout(modalTimerRef.current);
      setShowLostModal(false);
    });

    // ── Event: disconnected ───────────────────────────────────────────────
    s.on('disconnect', () => {
      setConnected(false);
      // Start the modal delay. If we reconnect before the timer fires,
      // the modal will never appear (see 'connect' handler above).
      clearTimeout(modalTimerRef.current);
      modalTimerRef.current = setTimeout(() => {
        setShowLostModal(true);
      }, DISCONNECT_MODAL_DELAY_MS);
    });

    // ── Event: connection error ───────────────────────────────────────────
    s.on('connect_error', (err) => {
      console.warn('[Socket.IO] Connection error:', err.message);

      const isAuthError = /unauthorized|jwt|token/i.test(err.message || '');
      if (!isAuthError) {
        authFailureCountRef.current = 0; // non-auth errors (network blips) don't count
        return;
      }

      authFailureCountRef.current += 1;
      if (authFailureCountRef.current >= MAX_AUTH_FAILURES) {
        // A fresh token still isn't being accepted — the user's session itself
        // has likely expired and can't be silently renewed. Stop retrying
        // (further attempts would just fail the same way) and tell them to reload.
        console.warn('[Socket.IO] Giving up after repeated auth failures — session likely expired.');
        s.disconnect();
        setUnrecoverable(true);
        setShowLostModal(true);
      }
    });

    setSocket(s);

    return () => {
      // Clean up on unmount or when the account changes
      clearTimeout(modalTimerRef.current);
      clearInterval(pingIntervalRef.current);
      s.removeAllListeners();
      s.disconnect();
      setSocket(null);
      setConnected(false);
    };
  }, [accountId, getAccessToken]);

  // ── Application-level keepalive ping ───────────────────────────────────────
  useEffect(() => {
    if (!socket || !connected) {
      clearInterval(pingIntervalRef.current);
      return;
    }

    // Send a ping at a fixed interval to verify the connection is truly alive.
    // The server replies with 'pong' — useful for custom latency monitoring.
    pingIntervalRef.current = setInterval(() => {
      socket.emit('ping');
    }, PING_INTERVAL_MS);

    return () => clearInterval(pingIntervalRef.current);
  }, [socket, connected]);

  return (
    <SocketContext.Provider value={{ socket, connected }}>
      {children}
      {/* Rendered by the provider so it's always available regardless of route */}
      <ConnectionLostModal
        show={showLostModal}
        unrecoverable={unrecoverable}
        onReload={() => window.location.reload()}
      />
    </SocketContext.Provider>
  );
}
